import { Injectable, InjectionToken, inject } from '@angular/core';
import { Span, TextMapPropagator, diag } from '@opentelemetry/api';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { Instrumentation, registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { defaultResource, resourceFromAttributes } from '@opentelemetry/resources';
import type { BufferConfig } from '@opentelemetry/sdk-trace-base';
import {
    BatchSpanProcessor,
    ConsoleSpanExporter,
    SpanExporter,
    SpanProcessor,
    StackContextManager,
    WebTracerProvider
} from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';

import { ConfigurationService, ShutdownService, UserSessionService } from '@mobi/rwc-utils-common-ng-jslib';

import { TelemetryUserContext } from '../telemetry-user-context.service';

import { TRACE_REQUEST_WHITELIST_REGEX } from './trace-state.interceptor';

export const SPAN_CONSOLE_EXPORTER = new InjectionToken<boolean>(
    'Prints spans to the console. This can be used for diagnostic purposes.'
);

export const SPAN_EXPORTER = new InjectionToken<SpanExporter>(
    'SPAN_EXPORTER token is required to export the spans to the correct endpoint.'
);

export const TRACING_PROPAGATOR = new InjectionToken<TextMapPropagator>('TRACING_PROPAGATOR', {
    factory: () => new W3CTraceContextPropagator()
});

// if the MTC endpoint change, please update it also (for system-test) in
// https://gitlab.com/diemobiliar/it/rwc/utils/rwc-utils-devtools-jslib/-/blob/main/libs/devtools/src/lib/shared/deliver-server-mock/deliver-server-mock.controller.ts
export const MTC_ACA_ENDPOINT = '/cos/telemetry-processoraca/data/rwc';

@Injectable()
export class OtelInstrumentationService {
    readonly #config: BufferConfig = {
        maxQueueSize: 200,
        maxExportBatchSize: 50,
        scheduledDelayMillis: 15_000,
        exportTimeoutMillis: 30_000
    };
    #configService = inject(ConfigurationService);
    #shutdownService = inject(ShutdownService);
    #userSessionService = inject(UserSessionService);
    #spanExporter = inject(SPAN_EXPORTER);
    #consoleExporter = inject(SPAN_CONSOLE_EXPORTER, { optional: true });
    #propagator = inject(TRACING_PROPAGATOR);
    #started = false;
    #userId = inject(TelemetryUserContext).getUserIds();

    constructor() {
        const processors: SpanProcessor[] = [new BatchSpanProcessor(this.#spanExporter, this.#config)];
        const isLocalDev = this.#configService.getSync('stage') === 'local-dev';
        if (isLocalDev) {
            processors.length = 0;
            console.info('TELEMETRY: Tracing disabled on local-dev environment');
        }
        if (this.#consoleExporter ?? isLocalDev) {
            processors.push(new BatchSpanProcessor(new ConsoleSpanExporter(), this.#config));
        }
        this.#createProvider(processors);
    }

    #createProvider(processors: SpanProcessor[]): void {
        const tkId = this.#configService.getTkNameId();
        const version = this.#configService.getSync('version');
        const sessionId = this.#userSessionService.getId();
        const resource = resourceFromAttributes({
            ...defaultResource().attributes,
            [ATTR_SERVICE_NAME]: tkId,
            [ATTR_SERVICE_VERSION]: version,
            'user.id': this.#userId,
            'user.session': sessionId
        });
        const provider = new WebTracerProvider({ resource, spanProcessors: processors });
        this.#shutdownService.beforeShutdown$.subscribe(() => provider.forceFlush());
        provider.register({
            propagator: this.#propagator,
            contextManager: new StackContextManager().enable()
        });
    }

    start(instrumentations?: Instrumentation[]): void {
        if (this.#started) {
            diag.warn('OtelInstrumentationService already started.');
            return;
        }
        const propagateTraceHeaderCorsUrls = TRACE_REQUEST_WHITELIST_REGEX;
        const applyCustomAttributesOnSpan: (span: Span) => void = span => {
            span.setAttributes({
                source_page: window.location.href
            });
        };
        const ignoreUrls = [new RegExp(MTC_ACA_ENDPOINT)];
        registerInstrumentations({
            instrumentations: [
                new XMLHttpRequestInstrumentation({
                    propagateTraceHeaderCorsUrls,
                    applyCustomAttributesOnSpan,
                    ignoreUrls
                }),
                new FetchInstrumentation({
                    propagateTraceHeaderCorsUrls,
                    applyCustomAttributesOnSpan,
                    ignoreUrls
                }),
                ...(instrumentations ?? [])
            ]
        });
        this.#started = true;
    }
}
